import React, { useMemo } from 'react';
import RankBadge from './RankBadge';
import StatChips from '../../../shared/components/StatChips';
import { perkText } from '../../../shared/utils/heroStats';
import { resolveAssetUrl } from '../../../shared/utils/assetUrl';
import '../styles/character.css';
import '../styles/merge.css';
import { useTranslation } from 'react-i18next';
import { moduleTypeLabel, partName } from '../../../shared/utils/gameLabels';

/**
 * Picks a part for one slot. Shows the part currently in the slot (with Remove),
 * then the inventory sorted by rank (strongest first).
 */
export default function InventoryPickerModal({ slotType, modules, current, onSelect, onRemove, onClose }) {
  const { t } = useTranslation();
  const typeLabel = moduleTypeLabel(t, slotType);
  const filtered = useMemo(() => modules
    .filter((item) => {
      if (!item.module) return false;
      if (slotType === 'HAND') return item.module.type === 'LEFT_HAND' || item.module.type === 'RIGHT_HAND';
      if (slotType === 'LEG') return item.module.type === 'LEFT_LEG' || item.module.type === 'RIGHT_LEG';
      return item.module.type === slotType;
    })
    .slice()
    .sort((a, b) => (b.rank || 0) - (a.rank || 0) || (a.module.name || '').localeCompare(b.module.name || '')),
  [modules, slotType]);

  return (
    <div className="inventory-picker-overlay" onClick={onClose}>
      <div className="inventory-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="inventory-picker-title">
          {current
            ? t('picker.replace', 'Replace {{part}}', { part: typeLabel })
            : t('picker.select', 'Select {{part}}', { part: typeLabel })}
        </div>

        {current && (
          <div className="inventory-picker-current" style={{ '--rank-color': current.rankColor }}>
            <div className="inventory-picker-current-icon">
              <RankBadge rank={current.rank || 0} color={current.rankColor} name={current.rankName} />
              <img src={resolveAssetUrl(current.imageUrl)} alt={partName(t, current)} />
            </div>
            <div className="inventory-picker-current-info">
              <div className="inventory-picker-current-name">{partName(t, current)}</div>
              <div className="inventory-picker-current-meta">
                <StatChips str={current.str} agi={current.agi} int={current.int} size="xs" />
              </div>
              {perkText(current.perkType, current.perkValue) && (
                <div className="perk-line">{perkText(current.perkType, current.perkValue)}</div>
              )}
            </div>
            <button type="button" className="inventory-picker-remove" onClick={onRemove}>
              {t('picker.remove', 'Remove')}
            </button>
          </div>
        )}

        <div className="inventory-picker-subtitle">
          {current ? t('picker.goes_back', 'The current part goes back to your inventory') : t('picker.choose', 'Choose from your inventory')}
        </div>

        <div className="inventory-picker-grid">
          {filtered.length === 0 ? (
            <div className="inventory-picker-empty">{t('picker.empty', 'No {{part}} modules in inventory', { part: typeLabel })}</div>
          ) : (
            filtered.map((item) => (
              <button
                key={item.id}
                className={`inventory-picker-item ${item.rank > 0 ? 'ranked' : ''}`}
                style={{ '--rank-color': item.rankColor }}
                onClick={() => onSelect(item)}
              >
                <RankBadge rank={item.rank || 0} color={item.rankColor} name={item.rankName} badgeImageUrl={item.rankBadgeImageUrl} />
                <img src={resolveAssetUrl(item.module.imageUrl)} alt={partName(t, item.module)} />
                <div className="inventory-picker-item-name">{partName(t, item.module)}</div>
                <StatChips str={item.str} agi={item.agi} int={item.int} size="xs" className="inventory-picker-item-stats" />
                {perkText(item.perkType, item.perkValue) && (
                  <div className="inventory-picker-item-perk">{perkText(item.perkType, item.perkValue)}</div>
                )}
                {item.quantity > 1 && <div className="inventory-picker-item-qty">x{item.quantity}</div>}
              </button>
            ))
          )}
        </div>

        <button className="inventory-picker-cancel" onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );
}
